import React from 'react'
import styled from '@emotion/styled'
import { debounce } from '@mobi/utils/functions'
import { random } from '@mobi/utils/number/random'
import { SlotComponent, SlotRef } from './Components/Slot'

type Props = {
  value: string
  startValue?: string
  /** Time in seconds */
  duration?: number
  /** Time in seconds */
  speed?: number
  dummyCharacters?: string[]
  dummyCharacterCount?: number
  /** Characters to NOT animate */
  separatorCharacters?: string[]
}

export const SlotCounterImpl = (
  {
    value,
    startValue,
    duration = 0.7,
    speed = 1.4,
    dummyCharacters,
    dummyCharacterCount = 6,
    separatorCharacters,
  }: Props,
  ref: React.Ref<SlotCounterRef>
) => {
  const isFirstRender = React.useRef(true)
  const slotCounterRef = React.useRef<HTMLSpanElement>(null)
  const slotRefs = React.useRef<SlotRef[]>([])

  const valueRef = React.useRef(value)
  const prevValueRef = React.useRef<string | undefined>(startValue)

  const [, setForceRender] = React.useState(0)
  const forceRender = React.useCallback(() => setForceRender(curr => ++curr), [])

  const dummyList = React.useMemo(
    () =>
      range(0, dummyCharacterCount * duration * speed - 1).map(i => {
        if (!dummyCharacters) return random(0, 9)
        const index = i >= dummyCharacters.length ? random(0, dummyCharacters.length) : i
        return dummyCharacters[index]
      }),
    [dummyCharacters, dummyCharacterCount, speed, duration]
  )

  if (valueRef.current !== value && !isFirstRender.current) {
    prevValueRef.current = valueRef.current
    valueRef.current = value
  }

  const valueList = value.split('')
  const prevValueList = prevValueRef.current?.split('') || []

  const isChangedValueLength = prevValueList.length !== valueList.length
  const isChangedValueIndexList: number[] = []

  const shouldUseStartValue = startValue !== null && isFirstRender.current

  valueList.forEach((_, i) => {
    const targetIndex = valueList.length - i - 1
    if (valueList[targetIndex] !== prevValueList[targetIndex] || isChangedValueLength) {
      isChangedValueIndexList.push(targetIndex)
    }
  })
  isChangedValueIndexList.reverse()

  const calculatedInterval = React.useMemo(
    () => Math.min(0.1, duration / valueList.length),
    [duration, valueList.length]
  )

  const refreshStyles = React.useCallback(() => {
    slotRefs.current.forEach(({ refreshStyles }) => refreshStyles())
  }, [])

  React.useImperativeHandle(ref, () => ({ startAnimation: forceRender, refreshStyles }))

  React.useEffect(() => {
    isFirstRender.current && forceRender()
    isFirstRender.current = false
  }, [value, forceRender])

  const handleFontHeightChange = React.useCallback(
    () => debounce(refreshStyles, 0),
    [refreshStyles]
  )

  const renderValueList = shouldUseStartValue ? prevValueList : valueList
  const startValueLengthDiff = (prevValueList.length || 0) - renderValueList.length

  const checkIsSeparatorCharacter = (value: string) => {
    return separatorCharacters ? separatorCharacters.includes(value) : isSeparatorCharacter === null
  }

  const isChangedValueIndexListWithoutSeparator = isChangedValueIndexList.filter(
    i => !checkIsSeparatorCharacter(renderValueList[i])
  )

  return (
    <SlotRootStyled ref={slotCounterRef}>
      {renderValueList.map((character, i) => {
        if (checkIsSeparatorCharacter(character)) {
          return <SeparatorStyled key={renderValueList.length - i - 1}>{character}</SeparatorStyled>
        }

        const prevValue = prevValueRef.current
        const shouldReverse = isDecrease(prevValue, value)

        const isChanged = isChangedValueIndexList.includes(i)
        const delay =
          (isChanged ? isChangedValueIndexListWithoutSeparator.indexOf(i) : 0) * calculatedInterval

        return (
          <SlotComponent
            key={renderValueList.length - i - 1}
            isChanged={isChanged}
            effectiveDuration={duration}
            delay={delay}
            value={character}
            startValue={shouldUseStartValue ? prevValueList?.[i + startValueLengthDiff] : undefined}
            dummyList={dummyList}
            reverse={shouldReverse}
            onFontHeightChange={handleFontHeightChange}
            speed={speed}
            duration={duration}
            ref={ref => ref && slotRefs.current.push(ref)}
          />
        )
      })}
    </SlotRootStyled>
  )
}

export const SlotCounter = React.memo(React.forwardRef(SlotCounterImpl))
export type { SlotCounterRef }

// =============
// Local Helpers
// =============

const isDecrease = (prevValue: string | undefined, newValue: string) => {
  if (!prevValue) return false

  if (isNumeric(prevValue) && isNumeric(newValue)) {
    return toNumeric(newValue) < toNumeric(prevValue)
  } else {
    return false
  }
}

const range = (start: number, end: number) => {
  const result = []
  for (let i = start; i < end; i += 1) {
    result.push(i)
  }
  return result
}

const toNumeric = (value: string | number) => {
  if (typeof value === 'number') return value

  const str = Array.isArray(value) ? value.join('') : value
  return Number(str.replace(/[,$]/g, ''))
}

const isNumeric = (value: string): value is string =>
  typeof value !== 'object' && !Number.isNaN(toNumeric(value))

const SEPARATOR_CHARACTERS = [',', '.', ' ']
const SPACE_UNICODE_REGEXP = /\u0020/
const NBSP_UNICODE_REGEXP = /\u00A0/
const NARROW_NO_BREAK_SPACE_UNICODE_REGEXP = /\u202F/

const isSeparatorCharacter = (value: string) =>
  SEPARATOR_CHARACTERS.includes(value) ||
  SPACE_UNICODE_REGEXP.test(value) ||
  NBSP_UNICODE_REGEXP.test(value) ||
  NARROW_NO_BREAK_SPACE_UNICODE_REGEXP.test(value)

// ======
// Styles
// ======

const SlotRootStyled = styled.span({
  display: 'inline-block',
  whiteSpace: 'nowrap',

  '[aria-hidden="true"]': { userSelect: 'none' },
})

const SeparatorStyled = styled.span({
  display: 'inline-block',
  verticalAlign: 'middle',
  textAlign: 'center',
})

// =====
// Types
// =====

interface SlotCounterRef {
  startAnimation: () => void
  refreshStyles: () => void
}
